import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import API_BASE_URL from "../config";
import logo from "../assets/pairfon-logo-horizontal-dark.png";
import PasswordInput from "../components/PasswordInput";
import "./AuthTheme.css";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupKind, setPopupKind] = useState("error"); // "error" | "verify"
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const nextRoute = searchParams.get("next");

  useEffect(() => {
    const token = localStorage.getItem("userToken");
    if (token) {
      const storedRole = (localStorage.getItem("userRole") || "").toLowerCase();
      navigate(storedRole === "support" || storedRole === "superadmin" ? "/ops/overview" : "/dashboard");
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_BASE_URL}/login`, { email, password });
      localStorage.setItem("userToken", res.data.token);
      if (res.data?.user?.role) localStorage.setItem("userRole", res.data.user.role);
      else localStorage.removeItem("userRole");
      if (res.data?.companyStatus) localStorage.setItem("companyStatus", res.data.companyStatus);
      else localStorage.removeItem("companyStatus");

      const role = (res.data?.user?.role || "").toLowerCase();
      if (res.data?.requiresCheckout) { navigate("/plans?checkout=1", { replace: true }); return; }
      if (nextRoute === "plans") { navigate("/plans", { replace: true }); return; }
      navigate(role === "support" || role === "superadmin" ? "/ops/overview" : "/dashboard");
    } catch (error) {
      if (error.response) {
        if (error.response.status === 403) {
          setPopupKind("verify");
          setPopupMessage("Your email is not verified. Please check your inbox.");
          setShowPopup(true);
        } else if (error.response.status === 401) {
          setPopupKind("error");
          setPopupMessage("Invalid email or password. Please try again.");
          setShowPopup(true);
        } else {
          setPopupKind("error");
          setPopupMessage(error.response.data?.error || "An error occurred. Please try again.");
          setShowPopup(true);
        }
      } else {
        setPopupKind("error");
        setPopupMessage("Network error. Please check your connection.");
        setShowPopup(true);
      }
    }
  };

  return (
    <div className="pf-auth">
      <div className="pf-shell">
        <aside className="pf-hero">
          <img className="pf-logo" src={logo} alt="Pairfon" />
          <div className="pf-hero-body">
            <span className="pf-badge">SMS · WhatsApp · iMessage</span>
            <h2>Your real number.<br />Every channel they text.</h2>
            <p>
              Connect your real numbers to a cloud gateway for{" "}
              <b style={{ color: "#a5a8ff" }}>SMS</b>, <b style={{ color: "#25d366" }}>WhatsApp</b> and{" "}
              <b style={{ color: "#0a84ff" }}>iMessage</b> — one dashboard, real numbers, no per-message fees.
            </p>
          </div>
          <div className="pf-stats">
            <div><div className="n">3</div><div className="l">Channels, one number</div></div>
            <div><div className="n">$0</div><div className="l">Per-message fees</div></div>
            <div><div className="n">2 min</div><div className="l">Setup time</div></div>
          </div>
        </aside>

        <div className="pf-card">
          <div>
            <h1 className="pf-title">Welcome back</h1>
            <p className="pf-sub">Sign in to your Pairfon dashboard.</p>
          </div>

          {nextRoute === "plans" && (
            <div className="pf-msg pf-msg--ok">Log in to finish checkout and activate your Pairfon plan.</div>
          )}

          <form className="pf-form" onSubmit={handleLogin}>
            <div className="pf-field">
              <label htmlFor="login-email">Email</label>
              <input id="login-email" className="pf-input" type="email" placeholder="you@company.com"
                value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required />
            </div>
            <div className="pf-field">
              <label htmlFor="login-pw">
                Password
                <button type="button" className="pf-link" onClick={() => navigate("/forgot-password")}>Forgot?</button>
              </label>
              <PasswordInput id="login-pw" placeholder="••••••••" value={password}
                onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
            </div>
            <button type="submit" className="pf-btn">Sign in</button>
          </form>

          <p className="pf-foot">
            New to Pairfon?{" "}
            <button type="button" className="pf-link" onClick={() => navigate("/signup")}>Create an account</button>
          </p>
        </div>
      </div>

      {showPopup && (
        <div className="pf-popup">
          <div className={`pf-popup-card ${popupKind === "error" ? "err" : ""}`}>
            <h2>{popupKind === "verify" ? "Email not verified" : "Login failed"}</h2>
            <p>{popupMessage}</p>
            {popupKind === "verify" && resendMsg && <p style={{ fontSize: "13px" }}>{resendMsg}</p>}
            <div className="pf-popup-actions">
              {popupKind === "verify" && (
                <button className="pf-btn" disabled={resending} onClick={async () => {
                  setResending(true); setResendMsg("");
                  try {
                    const res = await axios.post(`${API_BASE_URL}/resend-verification`, { email });
                    setResendMsg(res.data.message || "Verification email sent.");
                  } catch (err) {
                    setResendMsg(err.response?.data?.error || "Failed to resend.");
                  } finally { setResending(false); }
                }}>{resending ? "Sending..." : "Resend email"}</button>
              )}
              <button className="pf-btn pf-btn--ghost" onClick={() => { setShowPopup(false); setResendMsg(""); }}>
                {popupKind === "verify" ? "Close" : "Try again"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
